import { Injectable } from '@angular/core';
import { Effect, Actions, toPayload } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { Action, Store } from '@ngrx/store';

import { AuthenticationService } from '../services/authentication.service';
import { IApplicationState } from '../../stateManagement/states/applicationState';
import * as AuthenticationActions from '../../stateManagement/actions/data/authentication';
import * as applicationActions from '../../stateManagement/actions/containers/application';
import * as stageActions from '../../stateManagement/actions/data/stage';
import * as authenticationTypes from '../types';
import * as sharedTypes from '../../shared/types';

import * as _ from 'lodash';
import { environment } from '../../../environments/environment';
import * as commonConstants from '../../shared/types/constants';
import { LOCALSTORAGE_AUTH } from '../../../setup/configurations';

@Injectable()
export class AuthenticationEffects {
  portal: string = _.has(environment, 'portal') ? environment['portal'] : location.host;
  
  constructor(private actions$: Actions,
              private authenticationService: AuthenticationService,
              private store: Store<IApplicationState>) {}

  // tslint:disable-next-line:member-ordering
  @Effect()
  server_login$: Observable<Action> = this.actions$
      .ofType(AuthenticationActions.ActionTypes.SERVER_LOGIN)
      .switchMap( (action: Action) => {
          const loginData: authenticationTypes.ICredentials = Object.assign({}, action.payload, { portal: this.portal });
          return this.authenticationService.authenticate(loginData)
            .mergeMap((data: authenticationTypes.ILoginResult) => {
              this.authenticationService.saveAuthentication(data);
              let logInSuccessPayload: authenticationTypes.ILoginResult = _.cloneDeep(data);
              logInSuccessPayload.username = loginData.username;
              logInSuccessPayload.password = loginData.password;
              return [
                new applicationActions.ResetPopNotificationAction({}),
                new stageActions.ResetStageAction(1),
                new AuthenticationActions.ServerLoginSuccessAction(logInSuccessPayload)
              ];
            })
            .catch(error => {
              if (_.indexOf(commonConstants.ERROR_STATUS, error.status) != -1 && error.status != 401) {
                this.authenticationService.handleError(error.status,
                  'Login Failure', this.extractErrorMessage(error.json()),
                  new AuthenticationActions.ServerLoginAction(loginData));
              }
              return Observable.of(new AuthenticationActions.ServerLoginFailAction(error.json()));
          });
      });

  // tslint:disable-next-line:member-ordering
  @Effect()
  server_logout$: Observable<Action> = this.actions$
    .ofType(AuthenticationActions.ActionTypes.SERVER_LOGOUT)
    //.map(() => this.store.dispatch(new stageActions.ResetStageAction({})) )
    .map(() => {
      const logOutResult: authenticationTypes.IAuthenticationResult = {
        message: 'User has been logged out successfully'
      };

      return new AuthenticationActions.ServerLogoutSuccessAction(logOutResult);
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  server_reset_password$: Observable<Action> = this.actions$
    .ofType(AuthenticationActions.ActionTypes.SERVER_RESET_PASSWORD)
    .switchMap( (action: Action) => {
        const resetData: authenticationTypes.ICredentials = Object.assign({}, action.payload, { portal: this.portal });
        return this.authenticationService.resetPassword(resetData)
          .map((data: authenticationTypes.IAuthenticationResult) => {
            return new AuthenticationActions.ServerResetPasswordSuccessAction(data)
          })
          .catch(error => {
             if (_.indexOf(commonConstants.ERROR_STATUS, error.status) != -1  && error.status != 401) {
                this.authenticationService.handleError(error.status,
                  'Reset password Failure', this.extractErrorMessage(error.json()),
                  new AuthenticationActions.ServerResetPasswordAction(resetData));
             }
              return Observable.of(new AuthenticationActions.ServerResetPasswordFailAction(error.json()));
          });
    });

  // tslint:disable-next-line:member-ordering
  @Effect()
  server_update_password$: Observable<Action> = this.actions$
    .ofType(AuthenticationActions.ActionTypes.SERVER_UPDATE_PASSWORD)
    .switchMap( (action: Action) => {
        return this.authenticationService.updatePassword(<authenticationTypes.IUpdatePassword>action.payload)
          .map((data: authenticationTypes.IAuthenticationResult) => {
            this.authenticationService.updateLocalAuthentication(data);
            data.password = action.payload.newPassword;
            return new AuthenticationActions.ServerUpdatePasswordSuccessAction(data);
          })
          .catch(error => {
             if (_.indexOf(commonConstants.ERROR_STATUS, error.status) != -1  && error.status != 401) {
                this.authenticationService.handleError(error.status,
                  'Update password Failure', this.extractErrorMessage(error.json()),
                  new AuthenticationActions.ServerUpdatePasswordAction(action.payload));
             }
              return Observable.of(new AuthenticationActions.ServerUpdatePasswordFailAction(error.json()));
          });
    });

  @Effect()
  server_mfa_login$: Observable<Action> = this.actions$
    .ofType(AuthenticationActions.ActionTypes.SERVER_MFA_LOGIN)
    .switchMap((action: Action) => {
      const MFALoginData: authenticationTypes.ICredentials = Object.assign({}, action.payload, { portal: this.portal });

      return this.authenticationService.authenticate(MFALoginData)
        .mergeMap((data: authenticationTypes.ILoginResult) => {
          localStorage.removeItem(LOCALSTORAGE_AUTH);
          this.authenticationService.saveAuthentication(data);
          let MFALogInSuccessPayload: authenticationTypes.ILoginResult = _.cloneDeep(data);

          return [
            new applicationActions.ResetPopNotificationAction({}),
            new stageActions.ResetStageAction(1),
            new AuthenticationActions.ServerMfaLoginSuccessAction(MFALogInSuccessPayload)
          ];
        })
        .catch(error => {
          if (_.indexOf(commonConstants.ERROR_STATUS, error.status) != -1 && error.status != 401) {
            this.authenticationService.handleError(error.status,
              'MFA Login Failure', this.extractErrorMessage(error.json()),
              new AuthenticationActions.ServerMfaLoginAction(MFALoginData));
          }
          return Observable.of(new AuthenticationActions.ServerMfaLoginFailAction(error.json()));
        });
    });
  
  @Effect()
  save_mfa_auth_type$: Observable<Action> = this.actions$
    .ofType(AuthenticationActions.ActionTypes.SAVE_MFA_AUTH_TYPE)
    .switchMap((action: Action) => {
      return this.authenticationService.saveMfaAuthType(action.payload)
        .map((data: authenticationTypes.IMfaAuthCodeType) => {
          this.authenticationService.updateLocalAuthentication(data);
          return new AuthenticationActions.SaveMfaAuthTypeSuccessAction(data);
        })
        .catch(error => {
            if (_.indexOf(commonConstants.ERROR_STATUS, error.status) != -1) {
              this.authenticationService.handleError(error.status,
                'Failure to Save MFA Auth Type', this.extractErrorMessage(error.json()),
                new AuthenticationActions.SaveMfaAuthTypeAction(action.payload));
            }
            return Observable.of(new AuthenticationActions.SaveMfaTypeFailAction(error.json()));
        });
    });

  @Effect()
  resend_mfa_code$: Observable<Action> = this.actions$
    .ofType(AuthenticationActions.ActionTypes.RESEND_MFA_CODE)
    .switchMap((action: Action) => {
      return this.authenticationService.resendMfaCode()
        .mergeMap((data: sharedTypes.IResponse) => {
          return [
            new applicationActions.PopNotificationAction({type:'info', title: '', body: data.message}),
            new AuthenticationActions.ResendMfaCodeSuccessAction(data)
          ];
        })
        .catch(error => {
            if (_.indexOf(commonConstants.ERROR_STATUS, error.status) != -1) {
              this.authenticationService.handleError(error.status,
                'Failed to Re-Send MFA code', this.extractErrorMessage(error.json()),
                new AuthenticationActions.ResendMfaCodeAction(action.payload));
            }
            return Observable.of(new AuthenticationActions.ResendMfaCodeFailAction(error.json()));
        });
    });

  private extractErrorMessage(error): string {
    if (_.has(error, 'message')) {
      return error.message;
    }

    return JSON.stringify(error);
  }
}



// WEBPACK FOOTER //
// ./src/app/authentication/effects/authentication.effect.ts